import React from "react";
import { useDispatch } from "react-redux";

import { showNotification } from "jazasoft";
import { regex } from "@jazasoft/mui-table";

// local
import useQuery from "../../../hooks/useQuery.js";

import { REGEX_REPEAT, REGEX_SHRINKAGE } from "../../../utils/regex";
import { getDistinctValues, toFixed, cbChecklistItemSort, isEmpty, listFromCsv } from "../../../utils/helpers";
import { getRepeatSize, fabricRequired } from "../../../helpers/warehouseHelper.js";
import { getShrinkage } from "../../../helpers/orderHelper.js";
import { ChecklistType, Role, FabricDirection, CutOffSizeRatio } from "../../../utils/types";
import useMutation from "../../../hooks/useMutation.js";
import { FabClassification } from "../../../constants.js";
import useDeepMemo from "../../../hooks/useDeepMemo.js";
import useSetting from "../../../hooks/useSetting.js";
import SettingKeys from "../../../utils/SettingKeys.jsx";

export const choicesClassification = Object.values(FabClassification).map(v => ({ id: v, name: v }));

const getSortField = (item, trims, style) => {
  if (!item) return "";
  const repeatLength = item.repeatLength || 0;
  const repeatWidth = item.repeatWidth || 0;
  const warpShrinkage = item.warpShrinkage || 0;
  const weftShrinkage = item.weftShrinkage || 0;
  return `${trims ? `${trims}-` : ""}${style ? `${style}-` : ""}${item.fit}-${item.csv}-${item.width}-${item.fabPlacement}-${
    item.fabDirection
  }-${warpShrinkage}-${weftShrinkage}-${repeatLength}-${repeatWidth}`;
};

// Apply Index for Color coding
const applyColorIdx = rows => {
  let result = [];

  rows = rows.sort(cbChecklistItemSort);
  let idx = 0;
  let prev = null;
  for (let i = 0; i < rows.length; i++) {
    const curr = rows[i];
    if (prev) {
      if (prev.sortField !== curr.sortField) {
        idx++;
      }
    }
    result.push({ ...curr, colorIdx: idx });
    prev = curr;
  }
  return result;
};

const getSizeBreakup = sizeBreakup => {
  if (!sizeBreakup) return "";
  const sbList = JSON.parse(sizeBreakup);
  return sbList
    .filter(sb => sb.qty > 0)
    .sort((a, b) => a.serial - b.serial)
    .map(sb => `${sb.size}: ${sb.qty}`)
    .join(", ");
};

const isSizeBreakup = sizeBreakup => {
  if (!sizeBreakup) return null;
  const breakup = JSON.parse(sizeBreakup);
  return Object.keys(breakup)
    .map(key => breakup[key].qty)
    .some(e => e > CutOffSizeRatio);
};

const validate = (cuChecklist, checklistItems) => {
  if (!cuChecklist || !checklistItems || checklistItems.length === 0) return;
  const bodyItems = checklistItems.filter(e => e.styleName);
  if (checklistItems.length > 1) {
    let errors = [];
    let warnings = [];
    if (getDistinctValues(bodyItems.map(e => e.fit && e.fit.toLowerCase())).length > 1) {
      errors.push("Fit");
    }
    if (getDistinctValues(bodyItems.map(e => e.csv && e.csv.toLowerCase())).length > 1) {
      errors.push("CSV");
    }
    // if (getDistinctValues(checklistItems.map(e => e.fabPlacement && e.fabPlacement.toLowerCase())).length > 1) {
    //   errors.push("Fabric Placement");
    // }
    if (getDistinctValues(bodyItems.map(e => e.fabType && e.fabType.toLowerCase())).length > 1) {
      errors.push("Fabric Type");
    }
    if (getDistinctValues(bodyItems.map(e => e.width)).length > 1) {
      errors.push("Fabric Width");
    }
    if (getDistinctValues(bodyItems.map(e => e.repeat)).length > 1) {
      warnings.push("Repeat");
    }
    if (getDistinctValues(bodyItems.map(e => e.shrinkage)).length > 1) {
      errors.push("Shrinkage");
    }
    if (getDistinctValues(bodyItems.map(e => e.fabDirection)).length > 1) {
      errors.push("Fabric Direction");
    }

    if (cuChecklist.checklistType === ChecklistType.ESTIMATION) {
      const styleCount = getDistinctValues(bodyItems.map(e => e.styleName && e.styleName.toLowerCase())).length;
      if (styleCount > 1) {
        errors.unshift("Style");
      }
      if (getDistinctValues(bodyItems.map(e => e.customerPoRef?.toLowerCase())).length > 1) {
        errors.push("Customer PO Ref");
      }
      const poCount = getDistinctValues(bodyItems.map(e => e.poRef?.toLowerCase())).length;
      if (poCount > 1) {
        errors.push("PO Ref");
      }
      if (getDistinctValues(bodyItems.map(e => e.fabCategory?.toLowerCase())).length > 1) {
        errors.push("Fabric Category");
      }
      // Two body parts with same color cannot be merged
      const countMap = bodyItems
        .map(e => `${e?.poRef?.trim()}-${e.styleName?.trim()}-${e.styleColor?.trim()}`)
        .reduce((acc, key) => ({ ...acc, [key]: (acc[key] || 0) + 1 }), {});
      if (styleCount === 1 && poCount === 1 && Object.keys(countMap).filter(key => countMap[key] > 1).length) {
        return {
          error: `Selected rows cannot be merged. Shell Parts for one style cannot have same color.`
        };
      }
      // Two or more body parts with one having size ratio and another having actual size breakup cannot be merged
      if (getDistinctValues(bodyItems.map(e => isSizeBreakup(e.sizeBreakup)))?.length > 1) {
        return {
          error: "Rows having size breakup cannot be merged with rows having size ratio."
        };
      }
    } else if (cuChecklist.checklistType === ChecklistType.COSTING) {
      if (getDistinctValues(bodyItems.map(e => getSizeBreakup(e.sizeBreakup))).length > 1) {
        errors.push("Size Ratio");
      }
    }

    if (errors.length > 0) {
      return {
        error: `For merging multiple Checklist Items following fields must be same - [  ${errors.join(", ")}  ]`
      };
    }

    if (warnings.length > 0) {
      return {
        warning: `For merging multiple Checklist Items following fields should be same - [  ${warnings.join(", ")}  ]. Are you sure to proceed?`
      };
    }
  }

  let tentativeOrderQtyAvaialble = true;
  if (cuChecklist.checklistType === ChecklistType.ESTIMATION) {
    bodyItems.forEach(item => {
      if (!item.total) {
        if (!item.sizeBreakup) {
          tentativeOrderQtyAvaialble = false;
        } else {
          const sizeBreakup = JSON.parse(item.sizeBreakup);
          const total = Object.keys(sizeBreakup).reduce((acc, size) => acc + sizeBreakup[size].qty, 0);
          if (total < 15) {
            tentativeOrderQtyAvaialble = false;
          }
        }
      }
    });
  }
  if (!tentativeOrderQtyAvaialble) {
    return {
      error: "Total Qty is missing for one or more styles."
    };
  }
};

const choicesBoolean = [
  { id: "Yes", name: "Yes" },
  { id: "No", name: "No" }
];

const choicesFabDirection = Object.keys(FabricDirection).map(key => ({
  id: FabricDirection[key],
  name: FabricDirection[key]
}));

const getColumn = (dataKey, roles = [], checklistType, history, csvValues) => {
  const isMerchant = roles.includes(Role.MERCHANT);
  const isCutPlanner = roles.includes(Role.CUT_PLANNER);

  switch (dataKey) {
    case "poRef":
      return {
        dataKey: "poRef",
        title: "PO Ref",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        linkPath: row => row.estimationId && history.push(`/estimation/estimations/${row.estimationId}/view`)
      };
    case "styleName":
      return {
        dataKey: "styleName",
        title: "Style",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        linkPath: row =>
          checklistType === ChecklistType.COSTING
            ? row.costingId && history.push(`/estimation/costings/${row.costingId}/view`)
            : row.estimationId && history.push(`/estimation/estimations/${row.estimationId}/view`)
      };
    case "state":
      return {
        dataKey: "state",
        title: "Status",
        render: (value, shortValue, row) =>
          row?.styleName ? (
            <span
              style={{
                padding: "3px 6px",
                borderRadius: 4,
                color: "white",
                fontSize: "0.736607rem",
                backgroundColor: value === "New" ? "#4ca0eb" : value === "Pending" ? "#fc9b4c" : value === "Available" ? "#2bc397" : "green"
              }}>
              {value}
            </span>
          ) : null,
        filterOptions: { filter: true }
      };
    case "styleColor":
      return {
        dataKey: "styleColor",
        title: "Color",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        length: 20
      };
    case "label":
      return {
        dataKey: "label",
        title: "Label",
        inputType: !isMerchant ? "text-input" : undefined,
        disabled: (row, dataKey) => !row.styleName,
        length: 20,
        filterOptions: { filter: true }
      };
    case "fit":
      return {
        dataKey: "fit",
        title: "Fit/Block",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        length: 20,
        filterOptions: { filter: true }
      };
    case "supplierId":
      return {
        dataKey: "supplierId",
        title: "Supplier",
        render: (value, shortValue, row) => row?.supplier?.name,
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        filterOptions: { filter: true },
        length: 20
      };
    case "fabPlacement":
      return {
        dataKey: "fabPlacement",
        title: "Fab. Placement",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        filterOptions: { filter: true }
      };
    case "fabricPatternId":
      return {
        dataKey: "fabricPatternId",
        title: "Fab. Pattern",
        render: (value, shortValue, row) => row?.fabricPattern?.name,
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        filterOptions: { filter: true }
      };
    case "fabricClassification":
      return {
        dataKey: "fabricClassification",
        title: "Fab. Classification",
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        choices: choicesClassification,
        filterOptions: { filter: true, showValueOnly: false }
      };
    case "fabComposition":
      return {
        dataKey: "fabComposition",
        title: "Fab. Composition",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        length: 20
      };
    case "csv":
      return {
        dataKey: "csv",
        title: "CSV",
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        choices: csvValues.map(v => ({ id: v, name: v })),
        filterOptions: { filter: true, showValueOnly: false }
      };
    case "width":
      return {
        dataKey: "width",
        title: "Cuttable \nWidth (CM)",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        options: { type: "number" }
      };
    case "repeat":
      return {
        dataKey: "repeat",
        title: "Repeats (CM)",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        filterOptions: { filter: true },
        validate: regex(REGEX_REPEAT, "Invalid format. E.g - 1.5 X 2.5 or 1.5")
      };
    case "fabDirection":
      return {
        dataKey: "fabDirection",
        title: "Fab. \nDirection",
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        choices: choicesFabDirection,
        filterOptions: { filter: true }
      };
    case "wash":
      return {
        dataKey: "wash",
        title: "Wash",
        inputType: !isCutPlanner ? "select-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        choices: choicesBoolean,
        filterOptions: { filter: true, showValueOnly: false }
      };
    case "shrinkage":
      return {
        dataKey: "shrinkage",
        title: "Shrinkage",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        validate: regex(REGEX_SHRINKAGE, "Invalid format. E.g - L1.5% W2.5%")
      };
    case "matchingParameter":
      return {
        dataKey: "matchingParameter",
        title: "Matching Parameter",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New",
        filterOptions: { filter: true }
      };
    // case "customField1":
    //   return {
    //     dataKey: "customField1",
    //     title: "FTS/PTS/NTS",
    //     inputType: !isCutPlanner ? "text-input" : undefined,
    //     disabled: (row, dataKey) => row?.state !== "New"
    //   };
    case "styleDesc":
      return {
        dataKey: "styleDesc",
        title: "Style Description",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New"
      };
    case "remarks":
      return {
        dataKey: "remarks",
        title: "Remarks",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.state !== "New"
      };
    case "layCu":
      return {
        dataKey: "layCu",
        title: "Lay CU \nby CAD"
      };
    case "wastage":
      return {
        dataKey: "wastage",
        title: "Wastage (%)",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => ["New", "Pending"].includes(row?.state),
        options: { type: "number" }
      };
    case "costingCu":
      return { dataKey: "costingCu", title: "Costing CU" };
    case "orderQty":
      return { dataKey: "orderQty", title: "Order Qty" };
    case "bomCu":
      return { dataKey: "bomCu", title: "BOM CU" };
    case "extra":
      return {
        dataKey: "extra",
        title: "Extra (%)",
        inputType: !isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => ["New", "Pending"].includes(row?.state),
        options: { type: "number" }
      };
    case "fabRequired":
      return { dataKey: "fabRequired", title: "Fab. Required" };
    case "sizeBreakup":
      return {
        dataKey: "sizeBreakup",
        title: "Breakup",
        render: value => {
          if (isEmpty(value)) {
            return "-";
          } else {
            const sbList = JSON.parse(value);
            return sbList
              .filter(sb => sb.qty > 0)
              .sort((a, b) => a.serial - b.serial)
              .map(sb => `${sb.size}: ${sb.qty}`)
              .join(", ");
          }
        }
      };
    default:
      return { dataKey };
  }
};

const getColumns = (roles, cuChecklist) => {
  if (!roles || !cuChecklist) return [];
  const isMerchant = roles.includes(Role.MERCHANT);
  const isCutPlanner = roles.includes(Role.CUT_PLANNER);
  let columns = [];
  if (cuChecklist.checklistType === ChecklistType.COSTING) {
    columns = [
      "styleName",
      "state",
      "label",
      "styleColor",
      "fit",
      "supplierId",
      "fabPlacement",
      "fabricPatternId",
      "fabricClassification",
      "fabComposition",
      "csv",
      "width",
      "repeat",
      "fabDirection",
      "wash",
      "shrinkage",
      "matchingParameter",
      // "customField1",
      "styleDesc",
      "remarks",
      "layCu",
      "wastage",
      "costingCu",
      "sizeBreakup"
    ];
    if (isMerchant) {
      columns = columns.filter(c => !["label", "wash"].includes(c));
    }
  } else if (cuChecklist.checklistType === ChecklistType.ESTIMATION) {
    columns = [
      "poRef",
      "styleName",
      "state",
      "label",
      "styleColor",
      "fit",
      "supplierId",
      "fabPlacement",
      "fabricPatternId",
      "fabricClassification",
      "fabComposition",
      "csv",
      "width",
      "repeat",
      "fabDirection",
      "wash",
      "shrinkage",
      "matchingParameter",
      // "customField1",
      "styleDesc",
      "remarks",
      "orderQty",
      "costingCu",
      "layCu",
      "wastage",
      "bomCu",
      "extra",
      "fabRequired",
      "sizeBreakup"
    ];
    if (isMerchant) {
      columns = columns.filter(c => !["label", "wash"].includes(c));
    } else if (isCutPlanner) {
      columns = columns.filter(c => !["wastage", "finalBomCu", "extra", "fabRequired"].includes(c));
    }
  }
  return columns;
};

const useCuChecklistView = ({ id, roles, history }) => {
  const [version, setVersion] = React.useState(1); // for refresing cuChecklist
  const [dialogState, setDialogState] = React.useState({ active: false, cuChecklistItemId: null, action: "" }); // action: history, techSheets

  const dispatch = useDispatch();

  const { data: cuChecklist = {} } = useQuery({ url: `cuChecklists/${id}`, version });
  const { data: supplierList = [] } = useQuery({ url: `suppliers`, params: { size: 100 } });
  const { data: fabPatternList = [] } = useQuery({ url: `fabric-patterns`, params: { size: 100 } });

  const [mutate, { loading: loading1 }] = useMutation();
  const [download, { loading: loading2 }] = useMutation({ rawResponse: true });
  let busy = loading1 || loading2;

  const allowedCsvValues = useSetting(SettingKeys.ALLOWED_CSV_VALUES) || "";
  const allowedCsvList = listFromCsv(allowedCsvValues) || [];

  const onSubmit = (values, form, onSubmitComplete) => {
    const options = {
      url: `cuChecklists/${cuChecklist.id}`,
      method: "put",
      data: {
        ...cuChecklist,
        checklistItems: values
      }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Cu Checklist Items updated successfully`));
        onSubmitComplete();
        setVersion(version + 1);
      }
    });
  };

  const onSelectActionClick = (event, action, selectedItems, onActionComplete) => {
    if (action === "add") {
      const validation = validate(cuChecklist, selectedItems);
      if (validation?.error) {
        alert(validation.error);
        return;
      }
      if (validation?.warning) {
        /* eslint-disable-next-line */
        const result = confirm(validation.warning);
        if (!result) return;
      }
      const data = selectedItems.map(e => ({
        ...e,
        cuChecklistId: cuChecklist.id,
        customerId: cuChecklist.customer?.id,
        seasonId: cuChecklist.season?.id,
        productId: cuChecklist?.product?.id
      }));

      const options = {
        url: cuChecklist?.checklistType === ChecklistType.ESTIMATION ? "estimations" : "costings",
        method: "post",
        data
      };
      mutate(options, {
        onSuccess: () => {
          const message = `${cuChecklist.checklistType} created successfully.`;
          dispatch(showNotification(message));
          onActionComplete();
          setVersion(version + 1);
        }
      });
    } else if (action === "delete") {
      const options = {
        url: `cuChecklists/${id}/cuChecklistItems`,
        method: "delete",
        data: selectedItems
      };
      mutate(options, {
        onSuccess: () => {
          const message = `Cu Checklist Items deleted successfully.`;
          dispatch(showNotification(message));
          onActionComplete();
          setVersion(version + 1);
        }
      });
    }
  };

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (action === "edit") {
      const options = {
        url: `cuChecklists/${id}/cuChecklistItems/${row?.id}`,
        method: "put",
        data: row
      };
      mutate(options, {
        onSuccess: data => {
          const message = `Cu Checklist Item updated successfully.`;
          dispatch(showNotification(message));
          onActionComplete({ ...data, colorIdx: row.colorIdx });
          setVersion(version + 1);
        }
      });
    } else if (action === "history") {
      setDialogState({ active: true, cuChecklistItemId: row.id, action });
    }
  };

  const columns = useDeepMemo(() => {
    const columnList = getColumns(roles, cuChecklist);
    return columnList.map(key => ({
      choices: key === "supplierId" ? supplierList : key === "fabricPatternId" ? fabPatternList : [],
      ...getColumn(key, roles, cuChecklist.checklistType, history, allowedCsvList)
    }));
  }, [roles, allowedCsvList, cuChecklist, fabPatternList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(cuChecklist?.checklistItems)) return [];

    let rowList = cuChecklist.checklistItems.map(item => {
      let parent = item;
      if (!item.styleName) {
        parent = cuChecklist.checklistItems.find(e => e.styleName && e.itemNo === item.itemNo);
      }
      const trims = cuChecklist.checklistItems
        .filter(e => !e.styleName && e.itemNo === parent.itemNo)
        .sort((a, b) => (a.fabPlacement < b.fabPlacement ? -1 : 1))
        .map(e => e.fabPlacement)
        .join("-");
      const style = cuChecklist.checklistType === ChecklistType.ESTIMATION ? parent.styleName : null;
      const sortField = getSortField(parent, trims, style);
      let layCu, wastage, costingCu, bomCu, extra, fabRequired, finalizedState, finalizedOrderQty, finalizedMatchingParameter;
      if (cuChecklist.checklistType === ChecklistType.COSTING) {
        layCu = toFixed(item.cstPart?.layCu, 3);
        wastage = item.cstPart?.wastage;
        costingCu = toFixed(item.cstPart?.costingCu, 3);
        finalizedState = item.state;
        finalizedOrderQty = item.finalizedOrderQty;
        finalizedMatchingParameter = item.finalizedMatchingParameter;
      } else {
        costingCu = toFixed(item.estPart?.cstPart?.costingCu);
        // if ([EstimationState.AVAILABLE, EstimationState.COMPLETED].includes(item.state) && !!item.estPart?.layCu) {
        //   layCu = toFixed(item.estPart?.layCu, 3);
        //   wastage = item.estPart?.wastage;
        //   bomCu = toFixed(item.estPart?.orderingCu, 3);
        //   extra = item.estPart?.extra;
        //   fabRequired = fabricRequired(item.orderQty || parent?.orderQty, item.estPart?.orderingCu, extra);
        // } else {
        //   layCu = toFixed(item.estPart?.tentativeLayCu, 3);
        //   wastage = item.estPart?.tentativeWastage;
        //   bomCu = toFixed(item.estPart?.tentativeOrderingCu, 3);
        //   extra = item.estPart?.tentativeExtra;
        //   fabRequired = fabricRequired(item.total || parent?.total, item.estPart?.tentativeOrderingCu, extra);
        // }
        layCu = toFixed(item.layCu, 3);
        wastage = item.wastage;
        bomCu = toFixed(item.orderingCu, 3);
        extra = item.extra;
        finalizedOrderQty = item.finalizedOrderQty || item.orderQty;
        fabRequired =
          item.orderingCu && item.orderingCu > 0 ? fabricRequired(finalizedOrderQty || parent?.orderQty, item.orderingCu, extra) : undefined;
        finalizedState = item.finalizedState || item.state;
        finalizedMatchingParameter = item.finalizedMatchingParameter || item.matchingParameter;
      }

      return {
        ...item,
        modifiedDate: `${item.modifiedAt}-${item.cstPart?.modifiedAt || item.estPart?.modifiedAt}`,
        fabricPatternId: item.fabricPattern?.id,
        supplierId: item.supplier?.id,
        sortField,
        repeat: getRepeatSize(item),
        parentRepeat: getRepeatSize(parent),
        shrinkage: getShrinkage(item),
        // orderQty: item.orderQty || item.total,
        layCu,
        wastage,
        costingCu,
        bomCu,
        extra,
        fabRequired,
        state: finalizedState,
        orderQty: finalizedOrderQty || item.total,
        matchingParameter: finalizedMatchingParameter
      };
    });
    rowList = rowList.map(row => ({ ...row, similarCount: rowList.filter(e => e.sortField === row.sortField).length }));
    rowList = applyColorIdx(rowList);

    return rowList;
  }, [cuChecklist]);

  const onDownloadTechSheet = filename => {
    const options = {
      url: `estTechSheets`,
      method: "get",
      params: { filename, cuChecklistId: cuChecklist?.id },
      responseType: "arraybuffer"
    };
    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          let blob = new Blob([response.data], { type: "octet/stream" }),
            link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  const onUploadTechSheets = ({ techSheets }) => {
    var data = new FormData();
    for (let i = 0; i < techSheets.length; i++) {
      data.append("techSheets", techSheets[i]?.rawFile);
    }
    const options = {
      url: `estTechSheets`,
      method: "post",
      data,
      params: { cuChecklistId: cuChecklist?.id },
      headers: { "Content-Type": "multipart/form-data" }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("TechSheets updated successfully"));
        setVersion(version + 1);
        setDialogState({});
      }
    });
  };

  const onDeleteTechSheet = filename => {
    const options = {
      url: `estTechSheets`,
      method: "delete",
      params: { filename, cuChecklistId: cuChecklist?.id }
    };
    mutate(options, {
      onSuccess: () => {
        setVersion(version + 1);
        dispatch(showNotification("TechSheet deleted successfully."));
      }
    });
  };

  console.log({columns, rows, cuChecklist});

  return {
    cuChecklist,
    columns,
    rows,
    dialogState,
    busy,
    onSubmit,
    onSelectActionClick,
    onInlineActionClick,
    onDownloadTechSheet,
    onUploadTechSheets,
    onDeleteTechSheet,
    setDialogState
  };
};

export default useCuChecklistView;
